'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Icon, Tooltip } from '~/components/primitives';
import { Match } from 'effect';
import { useCatalog_EditMode_Session } from './session';
import { useMemo } from 'react';
import { useI18n } from '~/features/i18n';

/** Yellow banner shown above the booking layout while a catalog edit session
 * is active. Hidden below `lg` because `isEditing` is gated on the breakpoint. */
export const Catalog_EditMode_Banner: React.FC = () => {
  const $ = _c(2);
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n) {
    t0 = <Catalog_EditMode_ConditionalRender showWhen="editing">{() => <div role="status" data-testid="bizz-booking-edit-banner" className="flex items-start gap-3 border-b border-yellow-300 bg-yellow-100 px-6 py-5 text-base font-medium text-yellow-900 sm:px-15"><Icon name="settings" size="md" className="mt-0.5 shrink-0 text-yellow-900" /><p>{i18n._("bizz.booking.edit.banner.text2", undefined, {
            message: "You're editing the activity catalog. Use the sidebar to manage rules. Then, in the next step, click cards to add or exclude activities."
          })}</p></div>}</Catalog_EditMode_ConditionalRender>;
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

/** "Save & publish" button, shared by the step-2 sidebar footer and the sticky
 * view-tab strip so both stay in sync. Disabled while there is nothing to save;
 * a tooltip then explains why (the button alone reads as broken).
 *
 * Browsers don't dispatch mouse events from a `disabled` control, so the
 * disabled button gets `pointer-events-none` and the hover is picked up by the
 * tooltip's own wrapper element instead. */
export const Catalog_EditMode_SaveButton: React.FC<{
  testId: string;
}> = props => {
  const $ = _c(16);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  const isDisabled = !session.hasChanges || session.isSaving;
  const t0 = isDisabled && !session.isSaving;
  let t1;
  if ($[0] !== i18n) {
    t1 = i18n._("bizz.booking.edit.savePublish.noChanges", undefined, {
      message: "No changes to publish yet \u2014 adjust a rule or a card first."
    });
    $[0] = i18n;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = session.saveAndExit;
  const t3 = session.isSaving;
  const t4 = session.hasChanges ? "check" : undefined;
  const t5 = isDisabled ? "pointer-events-none" : undefined;
  const t6 = props.testId;
  let t7;
  if ($[2] !== i18n) {
    t7 = i18n._("bizz.booking.edit.savePublish", undefined, {
      message: "Save & publish"
    });
    $[2] = i18n;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== isDisabled || $[5] !== props.testId || $[6] !== session.isSaving || $[7] !== session.saveAndExit || $[8] !== t4 || $[9] !== t5 || $[10] !== t7) {
    t8 = <Button variant="tertiary" size="sm" onClick={t2} disabled={isDisabled} isLoading={t3} iconBefore={t4} className={t5} data-testid={t6}>{t7}</Button>;
    $[4] = isDisabled;
    $[5] = props.testId;
    $[6] = session.isSaving;
    $[7] = session.saveAndExit;
    $[8] = t4;
    $[9] = t5;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t0 || $[13] !== t1 || $[14] !== t8) {
    t9 = <Tooltip enabled={t0} content={t1} position="top" delay={200} className="max-w-xs">{t8}</Tooltip>;
    $[12] = t0;
    $[13] = t1;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
};
export const Catalog_EditMode_ConditionalRender: React.FC<{
  showWhen: 'editing' | 'browsing';
  children: React.ReactNode | React.FC;
}> = props => {
  const $ = _c(2);
  const session = useCatalog_EditMode_Session();
  const isEditing = session.step !== null;
  const shouldShow = Match.value(props.showWhen).pipe(Match.when("editing", () => isEditing), Match.when("browsing", () => !isEditing), Match.exhaustive);
  if (!shouldShow) {
    return null;
  }
  let t0;
  if ($[0] !== props.children) {
    t0 = typeof props.children === "function" ? <props.children /> : props.children;
    $[0] = props.children;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};